import React, { useState, useEffect } from 'react';
import { getStoredToken } from '../services/authSession';
import { FileText, Download, RefreshCw, Database, Calculator, AlertTriangle, Building2 } from 'lucide-react';
import API from '../services/apiBase';
import SCHEDULE_H_COLUMNS from './scheduleHHeaders';
import { useClient } from '../context/ClientContext';

const headerCategory = (label) => {
  const l = (label || '').toLowerCase();
  if (
    l.startsWith('start year') ||
    l.startsWith('internal contract number') ||
    l.startsWith('level 1 project number') ||
    l.startsWith('prime contract number') ||
    l.startsWith('sub contract number') ||
    l.startsWith('contract type') ||
    l.startsWith('prime or sub') ||
    l.startsWith('dod or non dod')
  ) return 'info';

  if (l.startsWith('direct labor') || l.includes('direct labor')) return 'labor';

  if (l.includes('fringe 101') || l.includes('fringe 100')) return 'fringe';

  if ((l.includes(' oh ') || l.endsWith(' oh') || l.includes('company oh') || l.includes('client oh')) && !l.includes('ceiling')) return 'overhead';

  if (l.includes('ceiling')) return 'ceiling';

  if (
    l.includes('odc') || l.includes('odcs') || l.includes('materials') || l.includes('subcontractor') ||
    l.includes('cio-cs-caf')
  ) return 'odc';

  if (
    l.includes('sm&h') || l.includes("g&a applied") || l.includes('subtotal direct costs & overhead') || l.includes("g&a base")
  ) return 'indirect';

  if (
    l.startsWith('total direct costs') || l.startsWith('total non-labor costs') || l === 'total costs'
  ) return 'total';

  if (
    l.startsWith("subtotal us gov't") || l.includes('ffp/commercial') || l.includes('total contract costs') || l.includes('total all costs') || l === 'check'
  ) return 'grand_total';

  return 'default';
};

const headerBg = (category) => {
  switch (category) {
    case 'info': return 'var(--gray-700)';
    case 'labor': return 'var(--primary-blue-dark)';
    case 'overhead': return 'var(--secondary-blue)';
    case 'fringe': return 'var(--success-green)';
    case 'odc': return 'var(--accent-orange)';
    case 'indirect': return 'var(--warning-amber)';
    case 'ceiling': return '#7c3aed';
    case 'total': return 'var(--gray-800)';
    case 'grand_total': return 'var(--gray-900)';
    default: return 'var(--gray-700)';
  }
};

const ScheduleHPage = () => {
  const { clientId, clientName, hasActiveClient } = useClient();
  const [recentGl, setRecentGl] = useState([]);
  const [glId, setGlId] = useState('');
  const [runId, setRunId] = useState('');
  const [contractLimit, setContractLimit] = useState('50');
  const [recentRuns, setRecentRuns] = useState([]);
  const [filteredRuns, setFilteredRuns] = useState([]); // Runs filtered by selected GL
  const [recentScheduleH, setRecentScheduleH] = useState([]);
  const [selectedScheduleHId, setSelectedScheduleHId] = useState('');
  const [loading, setLoading] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [scheduleHData, setScheduleHData] = useState(null);
  const [scheduleHColumns, setScheduleHColumns] = useState(SCHEDULE_H_COLUMNS);
  const [viewLimit, setViewLimit] = useState(500);
  const [error, setError] = useState(null);
  const [showDebug, setShowDebug] = useState(false);
  
  // Staleness tracking
  const [stalenessInfo, setStalenessInfo] = useState(null);
  const [latestVersions, setLatestVersions] = useState(null);
  const [showStaleWarning, setShowStaleWarning] = useState(false);
  
  const [contractSearch, setContractSearch] = useState('');
  const [contractTypeFilter, setContractTypeFilter] = useState('');
  const [hasDataOnly, setHasDataOnly] = useState(true);
  const [sortBy, setSortBy] = useState('internal_contract_number');
  const [sortOrder, setSortOrder] = useState('asc');

  const loadScheduleHSchema = async () => {
    try {
      const schemaUrl = clientId 
        ? `${API}/schedule-h/schema?client_id=${clientId}`
        : `${API}/schedule-h/schema`;
      const res = await fetch(schemaUrl);
      if (!res.ok) {
        setScheduleHColumns(SCHEDULE_H_COLUMNS);
        return;
      }
      const data = await res.json();
      const columns = Array.isArray(data?.columns)
        ? data.columns.filter((col) => col && col.key && col.label)
        : [];
      if (columns.length > 0) {
        setScheduleHColumns(columns);
      } else {
        setScheduleHColumns(SCHEDULE_H_COLUMNS);
      }
    } catch (e) {
      console.warn('Failed to load Schedule H schema', e);
      setScheduleHColumns(SCHEDULE_H_COLUMNS);
    }
  };

  const loadRecentGL = async () => {
    if (!clientId) return;
    try {
      const res = await fetch(`${API}/gl/list?limit=15&tenant_id=${clientId}`);
      if (res.ok) {
        const j = await res.json();
        setRecentGl(j.gl || []);
        if (!glId && (j.gl || []).length) {
          // Prefer GL with larger row count (real data vs test data)
          const sortedByRows = [...(j.gl || [])].sort((a, b) => (b.row_count || 0) - (a.row_count || 0));
          const bestGl = sortedByRows[0];
          if (bestGl && bestGl.gl_id) {
            setGlId(bestGl.gl_id);
          }
        }
      }
    } catch (e) {
      console.warn('Failed to load recent GL list', e);
    }
  };

  const loadRecentRuns = async (filterGlId = null) => {
    try {
      // Load all runs, with optional gl_id filter
      const url = filterGlId 
        ? `${API}/gl-calcs/runs/recent?limit=50&gl_id=${filterGlId}`
        : `${API}/gl-calcs/runs/recent?limit=50`;
      const res = await fetch(url);
      if (res.ok) {
        const data = await res.json();
        const runs = data.runs || [];
        setRecentRuns(runs);
        
        // If filtering by GL, auto-select the latest run for that GL
        if (filterGlId && runs.length > 0) {
          const latestForGl = runs.find(r => r.is_latest && r.gl_id === filterGlId);
          if (latestForGl) {
            setRunId(latestForGl.run_id);
          } else if (runs[0]) {
            setRunId(runs[0].run_id);
          }
          setFilteredRuns(runs.filter(r => r.gl_id === filterGlId));
        } else {
          setFilteredRuns(runs);
          if (!runId && runs.length > 0) {
            setRunId(runs[0].run_id);
          }
        }
      }
    } catch (e) {
      console.warn('Failed to load recent runs', e);
    }
  };

  // Load latest versions for staleness checking
  const loadLatestVersions = async () => {
    try {
      const res = await fetch(`${API}/schedule-h/latest-versions`);
      if (res.ok) {
        const data = await res.json();
        setLatestVersions(data);
      }
    } catch (e) {
      console.warn('Failed to load latest versions', e);
    }
  };

  // Check staleness of a Schedule H calculation
  const checkStaleness = async (scheduleHId) => {
    try {
      const res = await fetch(`${API}/schedule-h/staleness-check/${scheduleHId}`);
      if (res.ok) {
        const data = await res.json();
        setStalenessInfo(data);
        return data;
      }
    } catch (e) {
      console.warn('Failed to check staleness', e);
    }
    return null;
  };

  // Handle GL selection change - auto-select latest run for the selected GL
  const handleGlChange = async (newGlId) => {
    setGlId(newGlId);
    setRunId(''); // Reset run selection
    
    if (newGlId) {
      // Fetch the latest run specifically for this GL
      try {
        const res = await fetch(`${API}/gl-calcs/runs/latest/${newGlId}`);
        if (res.ok) {
          const data = await res.json();
          setRunId(data.run_id);
        }
      } catch (e) {
        console.warn('Failed to get latest run for GL', e);
      }
      
      // Also load filtered runs for the dropdown
      await loadRecentRuns(newGlId);
    } else {
      await loadRecentRuns();
    }
  };

  const loadRecentScheduleH = async () => {
    try {
      const res = await fetch(`${API}/schedule-h/list`);
      if (res.ok) {
        const data = await res.json();
        setRecentScheduleH(data.calculations || []);
      }
    } catch (e) {
      console.warn('Failed to load recent Schedule H', e);
    }
  };

  const loadScheduleH = async (scheduleHId) => {
    setLoading(true);
    setError(null);
    setStalenessInfo(null); // Reset staleness
    try {
      const params = new URLSearchParams();
      if (contractSearch) params.append('contract_search', contractSearch);
      if (contractTypeFilter) params.append('contract_type', contractTypeFilter);
      if (hasDataOnly) params.append('has_data_only', 'true');
      if (sortBy) params.append('sort_by', sortBy);
      if (sortOrder) params.append('sort_order', sortOrder);
      params.append('include_subtotals', 'true');
      const url = `${API}/schedule-h/${scheduleHId}${params.toString() ? '?' + params.toString() : ''}`;
      const res = await fetch(url);
      if (res.ok) {
        // Handle potential JSON parsing errors from truncated responses
        let data;
        try {
          const text = await res.text();
          data = JSON.parse(text);
        } catch (parseError) {
          console.error('[Schedule H] JSON parse error loading Schedule H:', parseError);
          setError('Failed to load Schedule H data. The response was incomplete. Please try again.');
          return;
        }
        setScheduleHData(data);
        setSelectedScheduleHId(scheduleHId);
        
        // Check staleness after loading
        await checkStaleness(scheduleHId);
      } else {
        const errorText = await res.text();
        setError(`Failed to load Schedule H: ${res.status} ${errorText}`);
      }
    } catch (e) {
      if (e.message && e.message.includes('JSON')) {
        setError('Failed to load Schedule H. The response was incomplete. Please try again.');
      } else {
        setError('Network error loading Schedule H');
      }
    } finally {
      setLoading(false);
    }
  };
  
  const applyFilters = () => {
    if (scheduleHData && scheduleHData.schedule_h_id) {
      loadScheduleH(scheduleHData.schedule_h_id);
    }
  };

  useEffect(() => {
    if (!hasActiveClient || !clientId) return;
    loadScheduleHSchema();
    loadRecentGL();
    loadRecentRuns();
    loadRecentScheduleH();
    loadLatestVersions();
  }, [clientId, hasActiveClient]);

  // Auto-load the newest Schedule H result for the active client.
  // Without this, the page can keep showing an older stale calculation
  // even after a successful recalc populated the backend correctly.
  useEffect(() => {
    if (!recentScheduleH || recentScheduleH.length === 0) return;
    const newest = recentScheduleH[0];
    if (!newest?.schedule_h_id) return;
    if (selectedScheduleHId || scheduleHData?.schedule_h_id) return;
    loadScheduleH(newest.schedule_h_id);
  }, [recentScheduleH]);

  const handleCalculate = async () => {
    if (!glId || !runId) {
      setError('Please select both GL dataset and calculation run');
      return;
    }

    setCalculating(true);
    setError(null);

    try {
      const requestBody = { 
        gl_id: glId, 
        run_id: runId,
        use_rules: true  // Always use rules-based calculation for accuracy
      };
      if (contractLimit !== 'all') {
        requestBody.limit = parseInt(contractLimit);
      }

      console.log('[Schedule H] Starting calculation with:', requestBody);
      console.log('[Schedule H] API endpoint:', `${API}/schedule-h/calculate`);

      // Use AbortController for timeout on long-running calculations
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 600000); // 10 minute timeout

      const token = getStoredToken();
      const res = await fetch(`${API}/schedule-h/calculate`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify(requestBody),
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      console.log('[Schedule H] Response status:', res.status);

      if (res.ok) {
        // Handle potential JSON parsing errors from truncated responses
        let data;
        try {
          const text = await res.text();
          data = JSON.parse(text);
        } catch (parseError) {
          console.error('[Schedule H] JSON parse error:', parseError);
          setError('Response was incomplete or corrupted. This can happen with large datasets. Try selecting fewer contracts (e.g., 50 or 100).');
          return;
        }
        
        console.log('[Schedule H] Success! Contract count:', data.contract_count);
        if (data && data.schedule_h_id) {
          await loadScheduleH(data.schedule_h_id);
          // Refresh the list of recent calculations
          await loadRecentScheduleH();
        } else {
          setScheduleHData(data);
        }
      } else {
        // Handle error responses - also protect against truncated error responses
        let errorMessage = 'Failed to calculate Schedule H';
        try {
          const errorText = await res.text();
          if (errorText) {
            const errorData = JSON.parse(errorText);
            errorMessage = errorData.detail || errorMessage;
          }
        } catch (parseError) {
          console.error('[Schedule H] Error response parse failed:', parseError);
          errorMessage = `Server error (${res.status}). The response was incomplete. Try with fewer contracts.`;
        }
        console.error('[Schedule H] Server error:', errorMessage);
        setError(errorMessage);
      }
    } catch (e) {
      console.error('[Schedule H] Fetch error:', e.name, e.message);
      if (e.name === 'AbortError') {
        setError('Calculation timed out. Try selecting fewer contracts or a lower limit.');
      } else if (e.message && e.message.includes('JSON')) {
        setError('Response was incomplete. This often happens with large datasets. Try selecting fewer contracts (50 or 100).');
      } else {
        setError(`Network error: ${e.message}. Please try again.`);
      }
    } finally {
      setCalculating(false);
    }
  };

  const handleRecalculate = async () => {
    // Always use the LATEST GL run for recalculation
    const currentGlId = scheduleHData?.gl_id || glId;
    
    if (!currentGlId) {
      setError('Cannot recalculate: missing GL dataset');
      return;
    }
    
    setCalculating(true);
    setError(null);

    try {
      // CRITICAL FIX: Always fetch and use the LATEST run for the GL
      let latestRunId = null;
      try {
        const latestRes = await fetch(`${API}/gl-calcs/runs/latest/${currentGlId}`);
        if (latestRes.ok) {
          const latestData = await latestRes.json();
          latestRunId = latestData.run_id;
          console.log('[Schedule H] Using LATEST run for recalculation:', latestRunId);
        }
      } catch (e) {
        console.warn('Failed to fetch latest run, falling back to current', e);
      }
      
      // Fallback to current run_id if latest fetch failed
      const finalRunId = latestRunId || scheduleHData?.run_id || runId;
      
      if (!finalRunId) {
        setError('Cannot recalculate: no calculation run found for this GL dataset');
        setCalculating(false);
        return;
      }
      
      // Update the UI selections to reflect what we're using
      if (scheduleHData?.gl_id) setGlId(scheduleHData.gl_id);
      setRunId(finalRunId);
    
      const requestBody = { 
        gl_id: currentGlId, 
        run_id: finalRunId,
        use_rules: true  // Always use rules-based calculation
      };
      // For recalculate, process all contracts
      // requestBody.limit = undefined; // No limit

      // Use AbortController for timeout on long-running calculations
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 600000); // 10 minute timeout

      const token = getStoredToken();
      const res = await fetch(`${API}/schedule-h/calculate`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify(requestBody),
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);

      if (res.ok) {
        const data = await res.json();
        if (data && data.schedule_h_id) {
          await loadScheduleH(data.schedule_h_id);
          // Refresh the list of recent calculations
          await loadRecentScheduleH();
        } else {
          setScheduleHData(data);
        }
      } else {
        // Handle error responses - protect against truncated error responses
        let errorMessage = 'Failed to recalculate Schedule H';
        try {
          const errorText = await res.text();
          if (errorText) {
            const errorData = JSON.parse(errorText);
            errorMessage = errorData.detail || errorMessage;
          }
        } catch (parseError) {
          console.error('[Schedule H] Error response parse failed:', parseError);
          errorMessage = `Server error (${res.status}). The response was incomplete.`;
        }
        setError(errorMessage);
      }
    } catch (e) {
      if (e.name === 'AbortError') {
        setError('Recalculation timed out. The calculation may take several minutes for large datasets.');
      } else if (e.message && e.message.includes('JSON')) {
        setError('Response was incomplete. This often happens with large datasets.');
      } else {
        setError('Network error recalculating Schedule H. The calculation may take several minutes for large datasets.');
      }
    } finally {
      setCalculating(false);
    }
  };

  const formatCurrency = (value) => {
    if (!value && value !== 0) return '—';
    const formatted = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(Math.abs(value));
    if (value < 0) {
      return <span style={{ color: 'var(--error-red)' }}>({formatted})</span>;
    }
    return formatted;
  };
  
  const handleExport = () => {
    if (!scheduleHData || !scheduleHData.schedule_h_id) {
      setError('No Schedule H data to export');
      return;
    }
    
    // Check staleness before export and warn user.
    // Phase 7: the latest-fringe-rate hint shows only when the run
    // summary actually carries it (legacy ``fringe_100_rate`` field).
    // Wizard clients whose runs don't expose that legacy field skip
    // the rate line gracefully — the "outdated" reasons block still
    // surfaces the actionable info.
    if (stalenessInfo?.is_stale) {
      const latestRate = stalenessInfo.latest?.fringe_100_rate;
      const rateLine = latestRate
        ? `Latest fringe rate: ${(latestRate * 100).toFixed(2)}%\n\n`
        : '';
      const confirmed = window.confirm(
        `⚠️ WARNING: This calculation may be outdated!\n\n` +
        `Reasons:\n${stalenessInfo.reasons.map(r => `• ${r}`).join('\n')}\n\n` +
        rateLine +
        `Click OK to export anyway, or Cancel to recalculate first.`
      );
      if (!confirmed) {
        return;
      }
    }
    
    const url = `${API}/schedule-h/${scheduleHData.schedule_h_id}/export`;
    setError(null);
    // Use fetch with auth header instead of window.open (which drops the token)
    (async () => {
      try {
        const res = await fetch(url);
        if (!res.ok) {
          const text = await res.text();
          throw new Error(`Export failed (${res.status}): ${text}`);
        }
        const blob = await res.blob();
        const blobUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = blobUrl;
        a.download = `Schedule_H_${scheduleHData.schedule_h_id.slice(0, 8)}.xlsx`;
        document.body.appendChild(a);
        a.click();
        // Small delay before cleanup to ensure download starts
        setTimeout(() => {
          document.body.removeChild(a);
          window.URL.revokeObjectURL(blobUrl);
        }, 200);
      } catch (err) {
        console.error('[Schedule H] Export error:', err);
        setError(err.message || 'Export failed');
      }
    })();
  };

  const getColumnValue = (contract, column) => {
    if (column.mapTo) return contract[column.mapTo];
    const keyMap = {
      internal_contract_number: 'internal_contract_number',
      level_1_project_number: 'level_1_project_number',
      contract_type: 'contract_type',
      dl_div02_company: 'dl_div02_company',
      dl_div02_company_unbillable: 'dl_div02_company_unbillable',
      dl_div02_client: 'dl_div02_client',
      dl_div02_client_unbillable: 'dl_div02_client_unbillable',
      dl_div04_company: 'dl_div04_company',
      dl_div04_company_unbillable: 'dl_div04_company_unbillable',
      dl_div04_client: 'dl_div04_client',
      dl_div04_client_unbillable: 'dl_div04_client_unbillable',
      dl_div06_company: 'dl_div06_company',
      dl_div06_company_unbillable: 'dl_div06_company_unbillable',
      dl_div06_client: 'dl_div06_client',
      dl_div06_client_unbillable: 'dl_div06_client_unbillable',
      dl_div08_company: 'dl_div08_company',
      dl_div08_company_unbillable: 'dl_div08_company_unbillable',
      dl_div10_company: 'dl_div10_company',
      dl_div10_company_unbillable: 'dl_div10_company_unbillable',
      dl_div10_client: 'dl_div10_client',
      dl_div10_client_unbillable: 'dl_div10_client_unbillable',
      dl_div11: 'dl_div11',
      dl_div11_unbillable: 'dl_div11_unbillable',
      dl_no_division: 'dl_no_division',
      dl_no_division_unbillable: 'dl_no_division_unbillable',
      total_direct_labor: 'total_direct_labor',
      unburdened_odcs: 'unburdened_odcs',
      unbillable_odcs: 'unbillable_odcs',
      sub_cons_labor: 'sub_cons_labor',
      sub_cons_odcs: 'sub_cons_odcs',
      materials_unbillable: 'materials_unbillable',
      sub_odcs_unbillable: 'sub_odcs_unbillable',
      cio_cs_caf: 'cio_cs_caf',
      total_direct_costs: 'total_direct_costs',
      total_non_labor_costs: 'total_non_labor_costs',
      fringe_div02_client: 'fringe_div02_client',
      oh_div02_client: 'oh_div02_client',
      oh_div02_company: 'oh_div02_company',
      fringe_div04_company: 'fringe_div04_company',
      oh_div04_company: 'oh_div04_company',
      fringe_div04_client: 'fringe_div04_client',
      oh_div04_client: 'oh_div04_client',
      fringe_div06_company: 'fringe_div06_company',
      oh_div06_company: 'oh_div06_company',
      fringe_div06_client: 'fringe_div06_client',
      oh_div06_client: 'oh_div06_client',
      fringe_div08_company: 'fringe_div08_company',
      oh_div08_company: 'oh_div08_company',
      fringe_div10_company: 'fringe_div10_company',
      oh_div10_company: 'oh_div10_company',
      fringe_div10_client: 'fringe_div10_client',
      fringe_101_div10_client: 'fringe_101_div10_client',
      oh_div10_client: 'oh_div10_client',
      fringe_div11: 'fringe_div11',
      oh_div11: 'oh_div11',
      fringe_no_division: 'fringe_no_division',
      smh: 'smh',
      direct_costs_overhead: 'direct_costs_overhead',
      ga_base_subtotal: 'ga_base_subtotal',
      ga: 'ga',
      total_costs: 'total_costs'
    };
    const dataKey = keyMap[column.key] || column.key;
    return contract[dataKey];
  };

  return (
    <div style={{ padding: '2rem', maxWidth: '100%', margin: '0 auto' }}>
      <div className="reconcile-header" style={{ marginBottom: '2rem', padding: '1.5rem 0', borderBottom: '1px solid var(--gray-200)' }}>
        <div className="reconcile-title">Schedule H</div>
        <div className="reconcile-meta">
          <div className="reconcile-meta-item">
            <FileText style={{ width: '1rem', height: '1rem' }} />
            <span className="reconcile-meta-label">Module:</span>
            <span>Direct Costs by Contract/Subcontract and Indirect Expense Applied at Claimed Rates</span>
          </div>
          <div className="reconcile-meta-item">
            <span className="reconcile-meta-label">Status:</span>
            <span className={`pro-badge ${scheduleHData ? 'pro-badge-success' : 'pro-badge-info'}`}>
              {scheduleHData ? 'Calculated' : 'Ready'}
            </span>
          </div>
        </div>
      </div>

      <div className="pro-card" style={{ marginBottom: '2rem' }}>
        <div className="pro-card-header">
          <h3 className="pro-card-title">
            <Database style={{ width: '1.25rem', height: '1.25rem', display: 'inline', marginRight: '0.5rem' }} />
            Select Data Sources or Load Saved Calculation
          </h3>
        </div>
        <div className="pro-card-content">
          <div style={{ marginBottom: '1.5rem', padding: '1rem', background: 'var(--gray-50)', borderRadius: 'var(--radius-md)' }}>
            <h4 style={{ fontSize: '0.875rem', fontWeight: '600', marginBottom: '1rem' }}>Calculate New Schedule H</h4>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 0.7fr', gap: '1.5rem', marginBottom: '1rem' }}>
              <div>
                <label style={{ fontSize: '0.875rem', fontWeight: '500', color: 'var(--gray-700)', marginBottom: '0.5rem', display: 'block' }}>
                  GL Dataset:
                </label>
                <select 
                  value={glId} 
                  onChange={e => handleGlChange(e.target.value)} 
                  className="pro-input"
                  disabled={calculating}
                >
                  <option value="">-- Choose GL Dataset --</option>
                  {recentGl.map((g, i) => (
                    <option key={i} value={g.gl_id}>
                      {g.filename || g.sheet_name || 'GL'} • {g.gl_id} • {g.row_count?.toLocaleString() || 0} rows
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label style={{ fontSize: '0.875rem', fontWeight: '500', color: 'var(--gray-700)', marginBottom: '0.5rem', display: 'block' }}>
                  Calculation Run (for rates):
                  {glId && <span style={{ color: 'var(--success-green)', marginLeft: '0.5rem', fontSize: '0.75rem' }}>✓ Auto-selects latest</span>}
                </label>
                <select 
                  value={runId} 
                  onChange={e => setRunId(e.target.value)} 
                  className="pro-input"
                  disabled={calculating}
                >
                  <option value="">-- Choose Calculation Run --</option>
                  {(glId ? filteredRuns : recentRuns).map((run, i) => (
                    <option key={i} value={run.run_id}>
                      {run.is_latest ? '★ LATEST • ' : ''}
                      {run.gl_id ? `${run.gl_id.slice(-6)} • ` : ''}
                      {run.run_id.slice(-12)} •
                      {run.created_at ? new Date(run.created_at).toLocaleDateString() : 'N/A'}
                      {/* Phase 7: only render the fringe-rate hint
                          when the run exposes the legacy
                          ``fringe_100_rate`` field. Wizard clients
                          whose runs use arbitrary fringe section IDs
                          omit this hint gracefully. */}
                      {run.fringe_100_rate ? ` • Fringe: ${(run.fringe_100_rate * 100).toFixed(2)}%` : ''}
                    </option>
                  ))}
                </select>
                {!glId && (
                  <p style={{ fontSize: '0.75rem', color: 'var(--warning-amber)', marginTop: '0.25rem' }}>
                    ⚠️ Select a GL dataset first to filter runs
                  </p>
                )}
              </div>

              <div>
                <label style={{ fontSize: '0.875rem', fontWeight: '500', color: 'var(--gray-700)', marginBottom: '0.5rem', display: 'block' }}>
                  Contract Limit:
                </label>
                <select 
                  value={contractLimit} 
                  onChange={e => setContractLimit(e.target.value)} 
                  className="pro-input"
                  disabled={calculating}
                >
                  <option value="50">50 contracts (Fast)</option>
                  <option value="100">100 contracts</option>
                  <option value="250">250 contracts</option>
                  <option value="500">500 contracts</option>
                  <option value="all">All contracts</option>
                </select>
              </div>
            </div>
            <button 
              className="pro-btn pro-btn-primary" 
              onClick={handleCalculate}
              disabled={calculating || !glId || !runId}
              style={{ fontSize: '0.875rem', padding: '0.625rem 1.25rem' }}
            >
              {calculating ? (
                <>
                  <RefreshCw style={{ width: '1rem', height: '1rem', marginRight: '0.5rem', animation: 'spin 1s linear infinite' }} />
                  Calculating...
                </>
              ) : (
                <>
                  <Calculator style={{ width: '1rem', height: '1rem', marginRight: '0.5rem' }} />
                  Calculate Schedule H
                </>
              )}
            </button>
          </div>

          {recentScheduleH.length > 0 && (
            <div style={{ padding: '1rem', background: 'var(--gray-50)', borderRadius: 'var(--radius-md)' }}>
              <h4 style={{ fontSize: '0.875rem', fontWeight: '600', marginBottom: '1rem' }}>Or Load Previously Calculated</h4>
              <div style={{ display: 'grid', gap: '0.5rem' }}>
                {recentScheduleH.slice(0, 5).map((calc, idx) => {
                  const isSelected = selectedScheduleHId === calc.schedule_h_id;
                  const isNewest = idx === 0;
                  const isCurrentRun = calc.run_id && calc.run_id === runId;
                  return (
                  <div 
                    key={idx}
                    onClick={() => loadScheduleH(calc.schedule_h_id)}
                    style={{
                      padding: '0.75rem',
                      background: isSelected ? 'var(--primary-blue)' : isNewest ? '#EFF6FF' : 'var(--white)',
                      color: isSelected ? 'var(--white)' : 'var(--gray-700)',
                      borderRadius: 'var(--radius-sm)',
                      cursor: 'pointer',
                      border: isNewest ? '2px solid var(--primary-blue)' : '1px solid var(--gray-200)',
                      transition: 'all 0.2s'
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.8125rem', gap: '1rem' }}>
                      <span style={{ fontWeight: '500' }}>
                        {isNewest ? '★ NEWEST • ' : ''}
                        {isCurrentRun ? 'CURRENT RUN • ' : ''}
                        {calc.contract_count} contracts • {calc.gl_id}
                      </span>
                      <span style={{ fontSize: '0.75rem', opacity: 0.8 }}>
                        {new Date(calc.calculated_at).toLocaleString()}
                      </span>
                    </div>
                    <div style={{ fontSize: '0.72rem', marginTop: '0.35rem', opacity: isSelected ? 0.9 : 0.75, fontFamily: 'monospace' }}>
                      Schedule H ID: {calc.schedule_h_id}
                    </div>
                  </div>
                )})}
              </div>
            </div>
          )}

          {error && (
            <div style={{ padding: '0.75rem', background: 'var(--error-red)', color: 'white', borderRadius: 'var(--radius-md)', marginTop: '1rem', fontSize: '0.875rem' }}>
              {error}
            </div>
          )}
        </div>
      </div>

      {scheduleHData && (
        <>
          {/* Staleness Warning Banner */}
          {stalenessInfo?.is_stale && (
            <div style={{ 
              padding: '1rem', 
              background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)', 
              border: '1px solid #f59e0b',
              borderRadius: 'var(--radius-md)', 
              marginBottom: '1.5rem',
              boxShadow: '0 2px 4px rgba(245, 158, 11, 0.1)'
            }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
                <span style={{ fontSize: '1.5rem' }}>⚠️</span>
                <div style={{ flex: 1 }}>
                  <h4 style={{ fontSize: '0.9375rem', fontWeight: '600', color: '#92400e', marginBottom: '0.5rem' }}>
                    This Calculation May Be Outdated
                  </h4>
                  <ul style={{ fontSize: '0.8125rem', color: '#78350f', margin: 0, paddingLeft: '1.25rem' }}>
                    {stalenessInfo.reasons.map((reason, i) => (
                      <li key={i}>{reason}</li>
                    ))}
                  </ul>
                  {/* Phase 7: the "Latest fringe rate" hint only
                      shows when the staleness response carries the
                      legacy ``fringe_100_rate`` field. Wizard clients
                      whose runs don't expose it omit this row
                      cleanly; the "rules-from" datestamp is still
                      shown standalone when present. */}
                  {(stalenessInfo.latest?.fringe_100_rate || stalenessInfo.current?.rules_created_at) && (
                    <p style={{ fontSize: '0.8125rem', color: '#78350f', marginTop: '0.5rem' }}>
                      {stalenessInfo.latest?.fringe_100_rate && (
                        <>
                          <strong>Latest fringe rate:</strong> {(stalenessInfo.latest.fringe_100_rate * 100).toFixed(2)}%
                          {stalenessInfo.current?.rules_created_at && <span> • </span>}
                        </>
                      )}
                      {stalenessInfo.current?.rules_created_at && (
                        <span><strong>Calculation used rules from:</strong> {new Date(stalenessInfo.current.rules_created_at).toLocaleDateString()}</span>
                      )}
                    </p>
                  )}
                  <button 
                    onClick={handleRecalculate}
                    disabled={calculating}
                    style={{
                      marginTop: '0.75rem',
                      padding: '0.5rem 1rem',
                      background: '#f59e0b',
                      color: 'white',
                      border: 'none',
                      borderRadius: 'var(--radius-sm)',
                      fontWeight: '500',
                      fontSize: '0.8125rem',
                      cursor: 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}
                  >
                    <RefreshCw style={{ width: '0.875rem', height: '0.875rem' }} />
                    Recalculate with Latest Data & Rules
                  </button>
                </div>
              </div>
            </div>
          )}
          
          {/* Rules Version Info (when not stale) */}
          {scheduleHData?.rules_created_at && !stalenessInfo?.is_stale && (
            <div style={{ 
              padding: '0.75rem 1rem', 
              background: 'var(--gray-50)', 
              borderRadius: 'var(--radius-md)', 
              marginBottom: '1rem',
              fontSize: '0.8125rem',
              color: 'var(--gray-600)',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              <span style={{ color: 'var(--success-green)' }}>✓</span>
              <span>Using latest rules (imported: {new Date(scheduleHData.rules_created_at).toLocaleDateString()})</span>
            </div>
          )}

          <div className="pro-card" style={{ marginBottom: '1.5rem' }}>
            <div className="pro-card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 className="pro-card-title">Filters & Options</h3>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.825rem' }}>
                  <input type="checkbox" checked={hasDataOnly} onChange={(e) => { setHasDataOnly(e.target.checked); setTimeout(applyFilters, 0); }} />
                  Hide zero-cost contracts
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.825rem' }}>
                  <input type="checkbox" checked={showDebug} onChange={(e) => setShowDebug(e.target.checked)} />
                  Debug view
                </label>
              </div>
            </div>
            <div className="pro-card-content">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr auto auto', gap: '1rem', alignItems: 'end' }}>
                <div>
                  <label style={{ fontSize: '0.875rem', fontWeight: '500', color: 'var(--gray-700)', marginBottom: '0.5rem', display: 'block' }}>
                    Search Contract:
                  </label>
                  <input
                    type="text"
                    value={contractSearch}
                    onChange={(e) => setContractSearch(e.target.value)}
                    placeholder="e.g. 11335"
                    className="pro-input"
                    style={{ fontSize: '0.875rem' }}
                  />
                </div>

                <div>
                  <label style={{ fontSize: '0.875rem', fontWeight: '500', color: 'var(--gray-700)', marginBottom: '0.5rem', display: 'block' }}>
                    Contract Type:
                  </label>
                  <select
                    value={contractTypeFilter}
                    onChange={(e) => setContractTypeFilter(e.target.value)}
                    className="pro-input"
                    style={{ fontSize: '0.875rem' }}
                  >
                    <option value="">All Types</option>
                    <option value="CPFF">CPFF</option>
                    <option value="T&M">T&M</option>
                    <option value="FFP">FFP</option>
                    <option value="Commercial">Commercial</option>
                  </select>
                </div>

                <div>
                  <label style={{ fontSize: '0.875rem', fontWeight: '500', color: 'var(--gray-700)', marginBottom: '0.5rem', display: 'block' }}>
                    Sort By:
                  </label>
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="pro-input"
                    style={{ fontSize: '0.875rem' }}
                  >
                    <option value="internal_contract_number">Contract Number</option>
                    <option value="total_direct_labor">Direct Labor</option>
                    <option value="total_costs">Total Costs</option>
                    <option value="contract_type">Contract Type</option>
                  </select>
                </div>

                <div>
                  <button 
                    className="pro-btn pro-btn-primary" 
                    onClick={applyFilters}
                    style={{ fontSize: '0.875rem', padding: '0.625rem 1.25rem' }}
                  >
                    Apply Filters
                  </button>
                <div>
                  <label style={{ fontSize: '0.875rem', fontWeight: '500', color: 'var(--gray-700)', marginBottom: '0.5rem', display: 'block' }}>
                    View Limit:
                  </label>
                  <select
                    value={viewLimit}
                    onChange={(e) => setViewLimit(parseInt(e.target.value))}
                    className="pro-input"
                    style={{ fontSize: '0.875rem' }}
                  >
                    <option value={200}>200 rows</option>
                    <option value={500}>500 rows</option>
                    <option value={1000}>1000 rows</option>
                    <option value={2000}>2000 rows</option>
                    <option value={5000}>5000 rows</option>
                  </select>
                </div>
                </div>
              </div>
            </div>
          </div>

          <div className="pro-card" style={{ overflow: 'visible' }}>
            <div className="pro-card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 className="pro-card-title">Schedule H Data</h3>
              <div style={{ display: 'flex', gap: '0.75rem' }}>
                <button 
                  className="pro-btn pro-btn-primary" 
                  onClick={handleRecalculate}
                  disabled={calculating}
                  style={{ fontSize: '0.875rem', padding: '0.625rem 1.25rem' }}
                  title="Recalculate using the latest rules from the database"
                >
                  {calculating ? (
                    <>
                      <RefreshCw style={{ width: '1rem', height: '1rem', marginRight: '0.5rem', animation: 'spin 1s linear infinite' }} />
                      Recalculating...
                    </>
                  ) : (
                    <>
                      <RefreshCw style={{ width: '1rem', height: '1rem', marginRight: '0.5rem' }} />
                      Recalculate with Latest Rules
                    </>
                  )}
                </button>
                <button 
                  className="pro-btn pro-btn-secondary" 
                  onClick={handleExport}
                  style={{ fontSize: '0.875rem', padding: '0.625rem 1.25rem' }}
                >
                  <Download style={{ width: '1rem', height: '1rem', marginRight: '0.5rem' }} />
                  Export to Excel
                </button>
              </div>
            </div>
            <div 
              className="pro-card-content" 
              style={{ 
                padding: 0, 
                overflow: 'auto', 
                maxHeight: 'calc(100vh - 400px)',
                outline: showDebug ? '2px dashed #eab308' : 'none'
              }}
            >
              <table className="financial-table" style={{ minWidth: 'max-content', position: 'relative', borderCollapse: 'separate', borderSpacing: 0, overflow: 'visible' }}>
                <thead style={{ position: 'sticky', top: 0, zIndex: 100 }}>
                  <tr>
                    <th
                      style={{
                        position: 'sticky',
                        top: 0,
                        left: 0,
                        zIndex: 101,
                        background: 'var(--gray-800)',
                        color: 'var(--white)',
                        padding: '0.875rem 0.75rem',
                        fontSize: '0.8125rem',
                        fontWeight: '600',
                        textAlign: 'left',
                        borderBottom: '2px solid var(--gray-600)',
                        minWidth: '60px'
                      }}
                    >
                      #
                    </th>
                    {scheduleHColumns.map((col) => {
                      const cat = headerCategory(col.label);
                      return (
                        <th 
                          key={col.key}
                          style={{ 
                            position: 'sticky', top: 0, zIndex: 100,
                            background: headerBg(cat), color: 'var(--white)',
                            padding: '0.875rem 0.75rem', fontSize: '0.8125rem', fontWeight: '600',
                            textAlign: 'left', borderBottom: '2px solid var(--gray-600)',
                            minWidth: '180px', whiteSpace: 'nowrap', boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                          }}
                        >
                          {col.label}
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {scheduleHData.contracts.filter(contract => {
                    const isSpecial = contract.is_section_header || contract.is_subtotal ||
                      contract.is_direct_subtotal ||
                      contract.is_grand_total || contract.is_final_grand_total ||
                      contract.is_unallowable || contract.is_unallowable_ga ||
                      contract.is_bp_ird || contract.is_bp_ird_total;
                    return isSpecial || (contract.total_direct_costs != null && contract.total_direct_costs !== 0);
                  }).slice(0, viewLimit).map((contract, rowIdx) => {
                    const isSectionHeader = contract.is_section_header;
                    const isDirectSubtotal = contract.is_direct_subtotal;
                    const isSubtotal = contract.is_subtotal || contract.is_bp_ird_total || isDirectSubtotal;
                    const isGrandTotal = contract.is_grand_total || contract.is_final_grand_total;
                    const isUnallowable = contract.is_unallowable || contract.is_unallowable_ga;
                    const isBpIrd = contract.is_bp_ird && !isSubtotal;
                    const bgColor = isSectionHeader
                      ? '#e0f2fe'
                      : isGrandTotal
                        ? 'var(--gray-900)'
                        : isUnallowable
                          ? '#fef3c7'
                          : isSubtotal || isBpIrd
                            ? 'var(--gray-100)'
                            : rowIdx % 2 === 0
                              ? 'var(--white)'
                              : 'var(--gray-50)';
                    const borderStyle = isGrandTotal
                      ? '3px double var(--gray-700)'
                      : isUnallowable
                        ? '2px solid #f59e0b'
                        : isSubtotal
                          ? '2px solid var(--gray-400)'
                          : isSectionHeader
                            ? '2px solid #bae6fd'
                            : '1px solid var(--gray-200)';

                    let rowLabel;
                    if (isSectionHeader) {
                      rowLabel = contract.section_type || '';
                    } else if (isGrandTotal) {
                      rowLabel = contract.is_final_grand_total ? 'TOTAL ALL COSTS' : 'TOTAL CONTRACT COSTS';
                    } else if (isUnallowable) {
                      rowLabel = contract.internal_contract_number || 'Unallowable Subject to G&A';
                    } else if (isSubtotal) {
                      rowLabel = contract.internal_contract_number;
                    } else if (isBpIrd || contract.is_bandp) {
                      rowLabel = contract.internal_contract_number || 'B&P';
                    } else {
                      rowLabel = rowIdx + 1;
                    }

                    return (
                      <tr
                        key={rowIdx}
                        style={{
                          background: bgColor,
                          borderTop: isSubtotal || isGrandTotal || isSectionHeader || isUnallowable ? borderStyle : undefined,
                          borderBottom: borderStyle,
                        }}
                      >
                        <td
                          style={{
                            position: 'sticky',
                            left: 0,
                            zIndex: 99,
                            background: bgColor,
                            padding: '0.75rem',
                            fontSize: '0.8125rem',
                            fontWeight: isGrandTotal || isUnallowable ? '700' : isSubtotal || isSectionHeader ? '600' : '500',
                            color: isGrandTotal
                              ? 'var(--white)'
                              : isUnallowable
                                ? '#92400e' // Amber-800 for unallowable text
                                : isSubtotal || isSectionHeader
                                  ? 'var(--gray-900)'
                                  : 'var(--gray-600)',
                            borderBottom: borderStyle,
                            borderRight: '1px solid ' + (isGrandTotal ? 'var(--gray-800)' : isUnallowable ? '#f59e0b' : 'var(--gray-200)'),
                          }}
                        >
                          {rowLabel}
                        </td>
                        {scheduleHColumns.map((col, colIdx) => {
                          const raw = getColumnValue(contract, col);
                          const isNumber = typeof raw === 'number';

                          let cellValue;
                          if (isNumber) {
                            cellValue = (
                              <span style={{ fontVariantNumeric: 'tabular-nums' }}>{formatCurrency(raw)}</span>
                            );
                          } else {
                            let text = raw ?? '';
                            // For section headers, leave data columns blank (title is rendered in the sticky index column)
                            if (isSectionHeader) {
                              text = '';
                            }
                            cellValue = isSectionHeader ? '' : (text || '—');
                          }

                          return (
                            <td
                              key={col.key}
                              style={{
                                padding: '0.75rem',
                                fontSize: '0.8125rem',
                                fontWeight: isGrandTotal ? '700' : isSubtotal || isSectionHeader ? '600' : '400',
                                color: isGrandTotal
                                  ? 'var(--white)'
                                  : isSubtotal || isSectionHeader
                                    ? 'var(--gray-900)'
                                    : 'var(--gray-700)',
                                textAlign: isNumber ? 'right' : 'left',
                                borderBottom: borderStyle,
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {cellValue}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {showDebug && (
                <div style={{ position: 'sticky', bottom: 0, background: '#fef9c3', color: '#1f2937', padding: '0.5rem', fontSize: '0.75rem', borderTop: '1px solid #f59e0b' }}>
                  Debug: sticky header active; header colors grouped by category; unmapped columns render blank.
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {!scheduleHData && !calculating && (
        <div className="pro-card">
          <div className="pro-card-content" style={{ textAlign: 'center', padding: '3rem' }}>
            <FileText style={{ width: '3rem', height: '3rem', margin: '0 auto 1rem', color: 'var(--gray-300)' }} />
            <p style={{ fontSize: '0.875rem', color: 'var(--gray-500)', marginBottom: '0.5rem' }}>
              No Schedule H calculated yet
            </p>
            <p style={{ fontSize: '0.75rem', color: 'var(--gray-400)' }}>
              Select GL dataset and calculation run above, then click Calculate
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ScheduleHPage;
